"use client";

import { useEffect, useState, useCallback } from "react";
import {
    COOKIE_CATEGORIES,
    getConsent,
    saveConsent,
    acceptAll,
    rejectAll,
} from "@/lib/cookie-consent";
import { useI18n } from "@/i18n";
import { useLangHref } from "@/i18n/useLangHref";

const styles = {
    page: {
        minHeight: "100vh",
        background: "#F5F7F5",
        paddingTop: 96,
        paddingBottom: 80,
    },
    container: {
        maxWidth: 820,
        margin: "0 auto",
        padding: "0 24px",
    },
    card: {
        background: "#ffffff",
        borderRadius: 16,
        border: "1px solid #ECEEEC",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        padding: "48px 56px",
    },
    title: {
        fontSize: 32,
        fontWeight: 800,
        color: "#111827",
        margin: 0,
        letterSpacing: "-0.01em",
    },
    subtitle: {
        fontSize: 14,
        color: "#6B7280",
        marginTop: 10,
        marginBottom: 28,
        paddingBottom: 24,
        borderBottom: "1px solid #F1F3F1",
    },
    intro: {
        fontSize: 15,
        lineHeight: 1.8,
        color: "#374151",
        marginTop: 8,
    },
    quickActions: {
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        marginTop: 24,
    },
    btn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: 42,
        padding: "0 20px",
        borderRadius: 12,
        fontSize: 14,
        fontWeight: 600,
        cursor: "pointer",
        border: "none",
        transition: "all 0.18s ease",
    },
    btnPrimary: { background: "#2F9E44", color: "#fff" },
    btnSecondary: { background: "#F4F6F4", color: "#111827", border: "1px solid #E5E7EB" },
    btnSave: { background: "#2F9E44", color: "#fff", height: 44, padding: "0 28px" },
    divider: { height: 1, background: "#F1F3F1", margin: "32px 0" },
    category: {
        border: "1px solid #ECEEEC",
        borderRadius: 14,
        padding: "20px 22px",
        marginBottom: 14,
        background: "#ffffff",
        transition: "border-color 0.18s ease",
    },
    categoryHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
    },
    categoryInfo: { flex: 1, minWidth: 0 },
    categoryLabelRow: { display: "flex", alignItems: "center", gap: 10 },
    categoryLabel: { fontSize: 16, fontWeight: 700, color: "#111827" },
    requiredBadge: {
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 6,
        background: "#FEF3C7",
        color: "#92400E",
        letterSpacing: "0.02em",
    },
    categoryDesc: { fontSize: 13.5, lineHeight: 1.65, color: "#6B7280", marginTop: 6 },
    toggleWrap: { flexShrink: 0 },
    rowActions: { display: "flex", justifyContent: "flex-end", marginTop: 10, gap: 10 },
    toast: {
        position: "fixed",
        top: 20,
        left: "50%",
        transform: "translateX(-50%)",
        padding: "12px 24px",
        borderRadius: 10,
        fontSize: 14,
        fontWeight: 500,
        zIndex: 9999,
        background: "#2F9E44",
        color: "#fff",
        boxShadow: "0 6px 20px rgba(47, 158, 68, 0.35)",
    },
};

function Toggle({ checked, onChange, disabled }) {
    const trackOn = "#2F9E44";
    const trackOff = "#D1D5DB";
    const trackDisabled = "#E5E7EB";
    const bg = disabled ? trackDisabled : checked ? trackOn : trackOff;

    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => !disabled && onChange(!checked)}
            style={{
                width: 44,
                height: 26,
                borderRadius: 999,
                background: bg,
                border: "none",
                padding: 2,
                cursor: disabled ? "not-allowed" : "pointer",
                position: "relative",
                transition: "background 0.18s ease",
                flexShrink: 0,
            }}
        >
            <span
                style={{
                    position: "absolute",
                    top: 2,
                    left: checked ? 20 : 2,
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    background: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                    transition: "left 0.18s ease",
                }}
            />
        </button>
    );
}

export default function CookiesPage() {
    const { t } = useI18n();
    const langHref = useLangHref();
    const [values, setValues] = useState({
        necessary: true,
        functional: false,
        analytics: false,
        marketing: false,
    });
    const [toast, setToast] = useState("");

    useEffect(() => {
        const c = getConsent();
        if (c) setValues(c.categories);
    }, []);

    const showToast = useCallback((msg) => {
        setToast(msg);
        setTimeout(() => setToast(""), 2400);
    }, []);

    const setOne = (key, v) => {
        setValues((prev) => ({ ...prev, [key]: v }));
    };

    const handleAcceptAll = () => {
        acceptAll();
        setValues({ necessary: true, functional: true, analytics: true, marketing: true });
        showToast(t("cookie.acceptAll"));
    };

    const handleRejectAll = () => {
        rejectAll();
        setValues({ necessary: true, functional: false, analytics: false, marketing: false });
        showToast(t("cookie.rejectAll"));
    };

    const handleSave = () => {
        saveConsent(values);
        showToast(t("cookie.saved"));
    };

    const catKeyMap = {
        necessary: "cookie.necessary",
        functional: "cookie.functional",
        analytics: "cookie.analytics",
        marketing: "cookie.marketing",
    };

    return (
        <div style={styles.page}>
            {toast && <div style={styles.toast}>{toast}</div>}
            <div style={styles.container}>
                <article style={styles.card}>
                    <h1 style={styles.title}>{t("cookie.title")}</h1>
                    <div style={styles.subtitle}>{t("cookie.subtitle")}</div>

                    <p style={styles.intro}>
                        {t("cookie.intro")}{" "}
                        <a href={langHref("/privacy")} style={{ color: "#2F9E44" }}>
                            {t("footer.legal.privacy")}
                        </a>
                        .
                    </p>

                    <div style={styles.quickActions}>
                        <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleAcceptAll}>
                            {t("cookie.acceptAll")}
                        </button>
                        <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={handleRejectAll}>
                            {t("cookie.rejectAll")}
                        </button>
                    </div>

                    <div style={styles.divider} />

                    {Object.values(COOKIE_CATEGORIES).map((cat) => {
                        const checked = values[cat.key];
                        const disabled = cat.required;
                        const i18nKey = catKeyMap[cat.key];
                        return (
                            <div key={cat.key} style={styles.category}>
                                <div style={styles.categoryHeader}>
                                    <div style={styles.categoryInfo}>
                                        <div style={styles.categoryLabelRow}>
                                            <span style={styles.categoryLabel}>{t(i18nKey)}</span>
                                            {cat.required && (
                                                <span style={styles.requiredBadge}>{t("cookie.necessaryTag")}</span>
                                            )}
                                        </div>
                                        <p style={styles.categoryDesc}>{t(`${i18nKey}Desc`)}</p>
                                    </div>
                                    <div style={styles.toggleWrap}>
                                        <Toggle
                                            checked={checked}
                                            disabled={disabled}
                                            onChange={(v) => setOne(cat.key, v)}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    <div style={styles.rowActions}>
                        <button style={{ ...styles.btn, ...styles.btnSave }} onClick={handleSave}>
                            {t("cookie.save")}
                        </button>
                    </div>
                </article>
            </div>
        </div>
    );
}
